import { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Lock, LogOut } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { usePoll, useRx } from "@/context/AuthContext";
import { fmt } from "@/lib/format";

const ZONE = {
  EXIT: { c: "#FF3366", t: "EXIT ZONE" },
  NEAR: { c: "#FFCC00", t: "NEAR EXIT" },
  HOLD: { c: "#00FF66", t: "HOLD" },
  NONE: { c: "#718096", t: "NO POSITION" },
};
const rsiColor = (v) => (v >= 80 ? "#FF3366" : v >= 70 ? "#FFCC00" : v <= 20 ? "#00B8FF" : v <= 30 ? "#7FD8FF" : "#ffffff");

function RsiMeter({ label, sub, value, testId }) {
  return (
    <div className="flex-1 min-w-[140px] rounded-xl border border-white/5 bg-black/30 p-3.5" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[10px] uppercase tracking-wider text-gray-500 truncate">{label}</span>
        <span className="font-data text-[10px] text-gray-500">{sub}</span>
      </div>
      <div className="font-data text-2xl font-bold mt-1" style={{ color: rsiColor(value) }} data-testid={`${testId}-value`}>{value?.toFixed(1) ?? "—"}</div>
      <div className="relative h-1.5 mt-2 rounded-full bg-white/5 overflow-hidden">
        <div className="absolute inset-y-0 left-0 w-[20%] bg-[#00B8FF]/15" />
        <div className="absolute inset-y-0 right-0 w-[20%] bg-[#FF3366]/15" />
        <div className="absolute top-1/2 -translate-y-1/2 h-3 w-1 rounded-full transition-[left] duration-700" style={{ left: `calc(${Math.min(100, Math.max(0, value || 0))}% - 2px)`, background: rsiColor(value), boxShadow: `0 0 8px ${rsiColor(value)}` }} />
      </div>
      <div className="flex justify-between font-data text-[9px] text-gray-600 mt-1"><span>20</span><span>50</span><span>80</span></div>
    </div>
  );
}

export default function RsiExitPanel({ segment, symbol }) {
  const { api, isBasic, me } = useRx();
  const [tf, setTf] = useState(5);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bump, setBump] = useState(0);
  const { data: v, error: rsiErr } = usePoll(isBasic && symbol ? `/rsi/${segment}/${symbol}?tf=${tf}` : null, 3000, [bump]);

  if (me && !isBasic) {
    return (
      <div className="rx-card p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="rsi-panel-locked">
        <div className="flex items-center gap-3"><Lock size={18} className="text-[#007AFF]" /><div><div className="font-semibold text-sm">RSI panel & Trade Exit</div><div className="text-xs text-gray-500">Spot, Futures and Strike RSI with one-tap exit. Available on Basic & Pro.</div></div></div>
        <Link to="/app/pricing" data-testid="rsi-unlock-btn" className="rounded-full bg-[#007AFF] px-5 py-2 text-xs font-bold hover:shadow-[0_0_20px_rgba(0,122,255,.5)] transition-shadow">Unlock</Link>
      </div>
    );
  }
  const z = ZONE[v?.zone || "NONE"];
  const exited = v?.exit;
  const canExit = v && v.direction !== "NONE" && !exited;
  const doExit = async () => {
    setBusy(true);
    try {
      const d = await api("post", "/positions/exit", { segment, symbol, tf });
      toast.success(`Exited ${d.instrument} @ ₹${fmt(d.exit_premium)}`);
      setBump((b) => b + 1);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not exit"); }
    setBusy(false);
    setOpen(false);
  };

  return (
    <div className="rx-hero-card p-4 lg:p-5" style={{ "--tone": z.c }} data-testid="rsi-exit-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <h3 className="rx-label">RSI monitor · {v?.symbol || symbol}</h3>
          <div className="inline-flex p-0.5 rounded-md bg-black/50 border border-white/10">
            {[5, 15].map((t) => (
              <button key={t} onClick={() => setTf(t)} data-testid={`rsi-tf-${t}m`} className={`px-2 py-0.5 rounded text-[10px] font-data font-bold transition-colors ${tf === t ? "bg-[#007AFF] text-white" : "text-gray-400 hover:text-white"}`}>{t}m</button>
            ))}
          </div>
        </div>
        {v && <span className="font-data text-[11px] text-gray-400">{v.action} · {v.direction === "NONE" ? "no direction" : v.direction}</span>}
      </div>
      <div className="flex flex-col lg:flex-row gap-3">
        <div className="flex flex-wrap gap-3 flex-1">
          <RsiMeter label={`${v?.symbol || symbol} Spot`} sub={v ? fmt(v.spot.ltp) : ""} value={v?.spot.rsi} testId="rsi-spot" />
          <RsiMeter label={`${v?.symbol || symbol} Futures`} sub={v ? fmt(v.futures.ltp) : ""} value={v?.futures.rsi} testId="rsi-futures" />
          <RsiMeter label={v?.strike || "Strike"} sub={v ? `₹${fmt(v.option.premium)}` : ""} value={v?.option.rsi} testId="rsi-strike" />
        </div>
        <div className="lg:w-64 shrink-0 rounded-xl border p-3.5 flex flex-col justify-between gap-3" style={{ borderColor: `${z.c}40`, background: `${z.c}0D` }}>
          {exited ? (
            <div data-testid="rsi-exited-status">
              <div className="flex items-center gap-2 text-sm font-bold text-[#00FF66]"><CheckCircle2 size={16} /> POSITION EXITED</div>
              <div className="font-data text-xs text-gray-300 mt-1.5">{exited.instrument} @ ₹{fmt(exited.exit_premium)}</div>
              <div className="font-data text-[11px] text-gray-500 mt-0.5">{new Date(exited.exited_at).toLocaleTimeString("en-IN")} · Spot {fmt(exited.exit_spot)}</div>
            </div>
          ) : (
            <div>
              <div className="font-data text-xs font-bold tracking-widest" style={{ color: z.c }} data-testid="rsi-zone">{z.t}</div>
              <div className="text-[11px] text-gray-400 mt-1" data-testid="rsi-zone-label">{rsiErr && !v ? <span className="text-[#FF3366]" data-testid="rsi-error">RSI unavailable: {String(rsiErr)}</span> : v?.zone_label || "Loading…"}</div>
              <div className="text-[10px] text-gray-600 mt-1">{v?.direction === "SHORT" ? "Exit near or below RSI 20" : "Exit near or above RSI 80"}</div>
            </div>
          )}
          <button onClick={() => setOpen(true)} disabled={!canExit} data-testid="trade-exit-btn"
            className={`inline-flex items-center justify-center gap-2 rounded-full py-2.5 text-sm font-bold transition-[box-shadow,filter] disabled:opacity-40 disabled:cursor-not-allowed ${v?.zone === "EXIT" ? "bg-[#FF3366] text-white shadow-[0_0_24px_rgba(255,51,102,.5)] animate-pulse" : v?.zone === "NEAR" ? "bg-[#FFCC00] text-black" : "bg-white/10 text-white hover:bg-white/15"}`}>
            <LogOut size={15} /> {exited ? "Exited" : "Trade Exit"}
          </button>
        </div>
      </div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="bg-[#11151C] border-white/10" data-testid="trade-exit-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Exit {v?.instrument}?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              Current premium ₹{fmt(v?.option.premium)} · Spot RSI {v?.spot.rsi} · Futures RSI {v?.futures.rsi} · Strike RSI {v?.option.rsi}.
              {v?.zone !== "EXIT" && v?.zone !== "NEAR" && " RSI is not in the exit zone yet."} This marks your position as exited in Robo-X; place the actual order with your broker.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="trade-exit-cancel" className="bg-transparent border-white/15">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); doExit(); }} disabled={busy} data-testid="trade-exit-confirm" className="bg-[#FF3366] hover:bg-[#e02d5a] text-white">{busy ? "Exiting…" : "Confirm exit"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
